import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Order, OrderForm, OrderSearchForm } from 'entities/order'

type OrdersResponse = PagingResponse & {
    results: Order[]
}

export function useFetchOrdersApi(searchForm?: Form<OrderSearchForm>): IndexApiSet<OrdersResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'orders/'
    const api = useIndexApi<OrdersResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm?.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function usePostOrderApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderForm>) => void } {
    const apiSet = usePostApi<BaseResponse, OrderForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderForm>) => {
        const apiPath = `orders/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export type OrderInspectForm = {
    staffId?: number
}

export function useInspectOrderApi(): ApiSet<BaseResponse> & { execute: (orderId: number, form: Form<OrderInspectForm>) => void } {
    const apiSet = usePostApi<BaseResponse, OrderForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (orderId: number, form: Form<OrderInspectForm>) => {
        const apiPath = `orders/${orderId}/inspect/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutOrderApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderForm>) => void } {
    const apiSet = usePutApi<BaseResponse, OrderForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderForm>) => {
        const apiPath = `orders/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutOrdersApi(): ApiSet<BaseResponse> & { execute: (form: Form<OrderForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, OrderForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<OrderForm[]>) => {
        const apiPath = `orders/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteOrderApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `orders/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}

export function useDownloadOrderListApi(): ApiSet<BaseResponse> & { execute: (orderId: number) => void } {
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (orderId: number): void => {
        const apiPath = `order_exports/${orderId}/`
        api.execute(apiPath, `orders_${orderId}`)
    }

    return { ...api, execute: execute }
}
